import { useCallback, useEffect, useState } from "react";
import { useParams, Link as RouterLink } from "react-router-dom";
import {
  Box, Typography, Button, TextField, Switch, FormControlLabel,
  CircularProgress, Chip, Alert, useTheme, alpha,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { ACCENT } from "./accent";
import { getCombination, simulate } from "./api/propulsion";
import ResultsView from "./ResultsView";
import { sweepModeled } from "./demo/model";
import { APP_BASE } from "./paths";

const SimulatorPage = () => {
  const theme = useTheme();
  const { id } = useParams();

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [form, setForm] = useState({
    all_up_weight_g: 850,
    num_motors: 4,
    coaxial: false,
    cells_series: 4,
    field_elevation_m: 0,
    air_temperature_c: 15,
  });
  const [result, setResult] = useState(null);
  const [curve, setCurve] = useState(null);
  const [premiumLocked, setPremiumLocked] = useState(false);
  const [simLoading, setSimLoading] = useState(false);
  const [simError, setSimError] = useState(null);

  useEffect(() => {
    getCombination(id)
      .then((d) => {
        setDetail(d);
        if (d.combination?.cells_series) {
          setForm((f) => ({ ...f, cells_series: d.combination.cells_series }));
        }
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [id]);

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: key === "coaxial" ? e.target.checked : e.target.value }));

  const run = useCallback(async () => {
    setSimLoading(true);
    setSimError(null);
    const opts = {
      all_up_weight_g: Number(form.all_up_weight_g),
      num_motors: Number(form.num_motors),
      coaxial: form.coaxial,
      cells_series: Number(form.cells_series),
      field_elevation_m: Number(form.field_elevation_m),
      air_temperature_c: Number(form.air_temperature_c),
    };
    try {
      const data = await simulate({ combination_id: id, ...opts });
      setResult(data.result);
      setPremiumLocked(Boolean(data.premium_locked));
      // Modeled reference curve for the charts, from this combination's parts.
      try {
        setCurve(
          detail
            ? sweepModeled(
                { motor: detail.motor, propeller: detail.propeller, esc: detail.esc, battery: detail.battery },
                opts
              )
            : null
        );
      } catch {
        setCurve(null);
      }
    } catch (e) {
      setSimError(e.message);
      setResult(null);
      setCurve(null);
    } finally {
      setSimLoading(false);
    }
  }, [id, form, detail]);

  const fieldSx = { flex: "1 1 140px", minWidth: 130 };

  return (
    <Box sx={{ p: 3, height: "100%", overflow: "auto" }}>
      <Button
        component={RouterLink}
        to={APP_BASE}
        startIcon={<ArrowBackIcon />}
        size="small"
        sx={{ color: theme.palette.text.secondary, textTransform: "none", mb: 2 }}
      >
        Propulsion Calculator
      </Button>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress color="secondary" size={28} />
        </Box>
      ) : error ? (
        <Typography color="error" variant="body2">
          {error}
        </Typography>
      ) : !detail ? (
        <Typography color="error" variant="body2">
          Could not load this combination.
        </Typography>
      ) : (
        <>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {detail.combination?.name || `${detail.motor?.name} + ${detail.propeller?.name}`}
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1.5, alignItems: "center" }}>
            {[
              ["Motor", detail.motor?.name],
              ["Prop", detail.propeller?.name],
              ["ESC", detail.esc?.name],
              ["Battery", detail.battery?.name],
            ].map(([k, v]) => (
              <Chip key={k} size="small" label={`${k}: ${v || "—"}`} variant="outlined" />
            ))}
            <Chip
              size="small"
              label={detail.has_measured ? "Measured data" : "Modeled"}
              sx={{
                backgroundColor: detail.has_measured ? alpha("#2e7d32", 0.15) : alpha(ACCENT, 0.12),
                color: detail.has_measured ? "#2e7d32" : ACCENT,
                fontWeight: 600,
              }}
            />
          </Box>

          {/* Setup form */}
          <Box
            sx={{
              display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center", mt: 3, p: 2,
              borderRadius: 2, border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
            }}
          >
            <TextField sx={fieldSx} size="small" type="number" label="All-up weight (g)"
              value={form.all_up_weight_g} onChange={set("all_up_weight_g")} />
            <TextField sx={fieldSx} size="small" type="number" label="# Motors"
              value={form.num_motors} onChange={set("num_motors")} />
            <TextField sx={fieldSx} size="small" type="number" label="Cells (S)"
              value={form.cells_series} onChange={set("cells_series")} />
            <TextField sx={fieldSx} size="small" type="number" label="Elevation (m)"
              value={form.field_elevation_m} onChange={set("field_elevation_m")} />
            <TextField sx={fieldSx} size="small" type="number" label="Air temp (°C)"
              value={form.air_temperature_c} onChange={set("air_temperature_c")} />
            <FormControlLabel
              control={<Switch checked={form.coaxial} onChange={set("coaxial")} size="small" />}
              label={<Typography variant="body2">Coaxial</Typography>}
            />
            <Button
              variant="contained"
              startIcon={simLoading ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
              onClick={run}
              disabled={simLoading}
              sx={{ backgroundColor: ACCENT, textTransform: "none", "&:hover": { backgroundColor: "#0058A5" } }}
            >
              Run simulation
            </Button>
          </Box>

          {simError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {simError}
            </Alert>
          )}

          <Box sx={{ mt: 3 }}>
            <ResultsView
              result={result}
              curve={curve}
              premiumLocked={premiumLocked}
              chartNote={detail?.has_measured ? "Modeled reference curve — tables use measured data" : "Modeled reference curve (±~15%)"}
            />
          </Box>
        </>
      )}
    </Box>
  );
};

export default SimulatorPage;
